import Backbone from 'backbone'
import template from './delete-confirmation.hbs'
import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import BusyButton from './busy-button'
import highlight from 'scoreboard/components/highlight'
import guard from 'scoreboard/components/unsaved/guard'
import FormView from 'scoreboard/components/form-view'

/**
 * @class DeleteConfirmation
 */
var DeleteConfirmation = Backbone.View.extend({
	tagName: 'form',

	className: 'delete-confirmation',

	events: {
		'submit': '_confirm'
	},

	/**
	 * The generic delete confirmation dialog for our application. You need to
	 * pass in a label or description when you create it, and listen for
	 * the 'confirm' action.
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {string} [options.title]
	 *
	 * @param {string} [options.label=Delete confirmation]
	 * If a label is provided the message will be a default message
	 * with the label text highlighted
	 *
	 * @param {string} options.description
	 * If a description is provided the label will be ignored,
	 * caller is responsible for any formatting.
	 *
	 * @param {string} [options.danger=Delete]
	 * This is the text that will be displayed on the delete button.
	 *
	 * @param {string} [options.extraBannerMessage]
	 * Text that will be displayed in the banner below the "this action can't be undone"
	 * message.
	 *
	 * @param{ String[]} [options.bannerMessages]
	 * Text that will be displayed in the banner below the "this action can't be undone"
	 * message, when more than one extra banner message is needed
	 *
	 * @fires confirm - when the user has passed all delete confirmation challenges
	 * and the resource should be physically deleted.
	 *
	 * @example <caption>with a label:</caption>
	 *
	 * var confirmation = new DeleteConfirmation(this.model.attributes)
	 * appDisplay.dialog(confirmation)
	 * confirmation.once('confirm', function() {
	 *     // update the UI
	 *     // close the confirmation dialog
	 *     confirmation.trigger('done')
	 * })
	 *
	 * @example <caption>with a description:</caption>
	 *
	 * var confirmation = new DeleteConfirmation({
	 *     description: i18n.get('scorecards.notes.reply').toLowerCase()
	 * })
	 * appDisplay.dialog(confirmation)
	 * confirmation.once('confirm', function(group) {
	 * 	   // update the UI
	 *     // close the confirmation dialog
	 *     confirmation.trigger('done')
	 * })
	 *
	 * @example <caption>with customized language if you need:</caption>
	 *
	 * var confirmation = new DeleteConfirmation({
	 *     title: 'admin.group.membership.remove',
	 *     message: i18n.get('admin.group.removeMembership', user, group),
	 *     danger: 'common.remove'
	 * })
	 *
	 */
	initialize: function(options) {
		this.options = options || {}

		this.formView = new FormView({
			toggleCancel: true,
			el: this.el,
			submit: this._confirm.bind(this),
			allowCleanSubmission: true
		})
	},

	render: function() {
		var message

		if (this.options.message) {
			// note: not escaped so HTML can be provided to highlight the
			// name of what is being deleted
			message = this.options.message
		} else if (this.options.label) {
			var substitution = highlight(this.options.label)
			message = i18n.get('common.delete.confirmation.message', substitution)
		} else {
			message = i18n.get('common.delete.confirmation', _.escape(this.options.description))
		}

		var context = {
			message: message,
			extraBannerMessage: this.options.extraBannerMessage,
			title: this.options.title || 'common.delete.confirmation.label',
			danger: this.options.danger || 'common.delete',
			showNotificationPrompt: this.options.showNotificationPrompt,
			bannerMessages: this.options.bannerMessages
		}

		this.$el.append(template(context))

		// issue #10182 set focus on delete button so hitting enter will submit the form
		this.$('.delete').focus()

		this.formView.start()

		return this
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.busyButton, this.formView], 'remove')
	},

	_confirm: function(e) {
		e.preventDefault()

		guard.stopGuarding()
		this.$el.addClass('is-submitting-form') // Doesn't use form-view so we need to add class manually
		this.busyButton = new BusyButton({ el: this.$('.button-danger') }).loading()
		this.trigger('confirm')
	}

})

export default DeleteConfirmation

DeleteConfirmation.__filename__ = 'components/delete-confirmation.js'
